import {useState, useEffect} from "react"
import {ApplicationDocumentSide} from "../resources/application"
import {Rotation, useToolbar, Zoom} from "./useToolbar"

export function useImageToolbar(
	imgRef: React.RefObject<HTMLImageElement>,
	isHorizontalImage: boolean,
	imageKey?: string
) {
	const {zoom, rotation} = useToolbar(ApplicationDocumentSide.Front)
	const {position} = useDraggableImage(imgRef, zoom, rotation, imageKey)
	const baseData = {zoom, rotation}
	const isSidewaysRotation = rotation.val === 90 || rotation.val === 270
	const aspectRatio = isSidewaysRotation || !isHorizontalImage ? "1" : "auto"

	return {
		...baseData,
		aspectRatio,
		transformOrigin: "center center",
		transform: `translate(${position.x}px, ${position.y}px) rotate(${rotation.val}deg) scale(${zoom.val})`,
	}
}

/**
 * Calculate symmetric drag bounds for a center-origin transformed image.
 * The image scales (and rotates) around its center, so overflow is symmetric
 * on both sides. Rotation 90/270 swaps width and height for the visual bounds.
 */
const calculateDragBounds = (image: HTMLImageElement, zoomVal: number, rotationVal: number) => {
	const container = image.parentElement
	if (!container) return null

	const containerRect = container.getBoundingClientRect()
	const containerWidth = containerRect.width
	const containerHeight = containerRect.height

	const baseWidth = image.offsetWidth
	const baseHeight = image.offsetHeight

	const isSidewaysRotation = rotationVal === 90 || rotationVal === 270
	const effectiveWidth = (isSidewaysRotation ? baseHeight : baseWidth) * zoomVal
	const effectiveHeight = (isSidewaysRotation ? baseWidth : baseHeight) * zoomVal

	const overflowX = Math.max(0, (effectiveWidth - containerWidth) / 2)
	const overflowY = Math.max(0, (effectiveHeight - containerHeight) / 2)

	return {
		minX: -overflowX,
		maxX: overflowX,
		minY: -overflowY,
		maxY: overflowY,
	}
}

/**
 * Clamp a position value within bounds
 * When scaled image <= container (min >= max), returns centerValue (no dragging needed)
 */
const clampPosition = (value: number, min: number, max: number, centerValue = 0) =>
	min < max ? Math.max(min, Math.min(max, value)) : centerValue

const useDraggableImage = (
	imgRef: React.RefObject<HTMLImageElement>,
	zoom: Zoom,
	rotation: Rotation,
	imageKey?: string
) => {
	const [position, setPosition] = useState({x: 0, y: 0})

	// Clamp position when zoom/rotation/image changes to prevent empty space
	useEffect(() => {
		const image = imgRef.current
		if (!image) return

		const bounds = calculateDragBounds(image, zoom.val, rotation.val)
		if (!bounds) return

		setPosition((pos) => ({
			x: clampPosition(pos.x, bounds.minX, bounds.maxX),
			y: clampPosition(pos.y, bounds.minY, bounds.maxY),
		}))
	}, [imgRef, zoom.val, rotation.val, imageKey])

	useEffect(() => {
		const image = imgRef.current
		if (!image) return

		let isDragging = false
		let prevPosition = {x: 0, y: 0}

		const handleMouseDown = (e: MouseEvent) => {
			isDragging = true
			prevPosition = {x: e.clientX, y: e.clientY}
		}

		const handleMouseMove = (e: MouseEvent) => {
			if (!isDragging || !image) return

			const deltaX = e.clientX - prevPosition.x
			const deltaY = e.clientY - prevPosition.y
			prevPosition = {x: e.clientX, y: e.clientY}

			const bounds = calculateDragBounds(image, zoom.val, rotation.val)
			if (!bounds) return

			setPosition((pos) => ({
				x: clampPosition(pos.x + deltaX, bounds.minX, bounds.maxX, (bounds.minX + bounds.maxX) / 2),
				y: clampPosition(pos.y + deltaY, bounds.minY, bounds.maxY, (bounds.minY + bounds.maxY) / 2),
			}))
		}

		const handleMouseUp = () => {
			isDragging = false
		}

		image.addEventListener("mousedown", handleMouseDown)
		// Use document instead of image to handle mouse movement on the whole page when dragging the image
		document.addEventListener("mousemove", handleMouseMove)
		document.addEventListener("mouseup", handleMouseUp)

		return () => {
			image.removeEventListener("mousedown", handleMouseDown)
			document.removeEventListener("mousemove", handleMouseMove)
			document.removeEventListener("mouseup", handleMouseUp)
		}
	}, [imgRef, zoom.val, rotation.val, imageKey])

	return {position}
}
